/* c8 ignore file */
import { ShiftTypeDTO, UserRightsEnum } from '@/generated'
import { AuthenticationService } from '@/services/AuthenticationService'
import { useUserStore } from '@/stores/user'
import type { RouteLocationNormalized, NavigationGuardNext } from 'vue-router'
import { routeNames } from './routeNames'
import { storeToRefs } from 'pinia'
import { useHospitalsStore } from '@/stores/hospitals'
import { usePluginsStore } from '@/stores/plugins'

const KEYCLOAK_CALLBACK_PARAMS = ['code', 'state', 'session_state', 'iss'] as const

export async function globalRouterGuards() {
  await AuthenticationService.updateAccessToken()
}

export function hasUserGodModeShift(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const userStore = useUserStore()

  if (userStore.user?.currentShift === ShiftTypeDTO.GOD_MODE) {
    next('/')
  } else {
    next()
  }
}

export function hasUserChiefPhysicianShift(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const userStore = useUserStore()

  if (userStore.user?.currentShift !== ShiftTypeDTO.CHIEF_PHYSICIAN) {
    next('/')
  } else {
    next()
  }
}

export function hasUserHospitalItRole(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const userStore = useUserStore()

  if (
    userStore.user?.currentRole !== UserRightsEnum.HOSPITAL_IT &&
    userStore.user?.currentRole !== UserRightsEnum.TCC_IT
  ) {
    next('/')
  } else {
    next()
  }
}

export function hasUserTccItRole(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const userStore = useUserStore()

  if (userStore.user?.currentRole !== UserRightsEnum.TCC_IT) {
    next('/')
  } else {
    next()
  }
}

export function restrictFromHospitalAdmin(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const userStore = useUserStore()

  if (
    (userStore.user?.currentRole === UserRightsEnum.HOSPITAL_IT ||
      userStore.user?.currentRole === UserRightsEnum.TCC_IT) &&
    to.name !== routeNames.HOSPITAL_ADMIN
  ) {
    if (userStore.user?.currentRole === UserRightsEnum.HOSPITAL_IT) {
      next({ name: routeNames.ADMIN_USERS })
    } else {
      next({ name: routeNames.ADMIN_HOSPITALS })
    }
  } else {
    next()
  }
}

export function hasGodModeShiftAndNotHospitalAdmin(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const userStore = useUserStore()

  if (
    (userStore.user?.currentRole === UserRightsEnum.HOSPITAL_IT ||
      userStore.user?.currentRole === UserRightsEnum.TCC_IT) &&
    to.name !== routeNames.HOSPITAL_ADMIN
  ) {
    next({ name: routeNames.HOSPITAL_ADMIN })
  } else if (userStore.user?.currentShift === ShiftTypeDTO.GOD_MODE) {
    next('/')
  } else {
    next()
  }
}

export function hasListPatientsPermission(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const { canListPatients } = storeToRefs(useUserStore())

  if (!canListPatients.value) {
    next('/')
  } else {
    next()
  }
}

export function hasAddPatientsPermission(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const { hasHospitalPhysicianRole, hasAddPatientsPermissions } = storeToRefs(useUserStore())

  if (hasHospitalPhysicianRole.value && hasAddPatientsPermissions.value) {
    next()
  } else {
    next('/')
  }
}

export function hasSendConsultationsPermission(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const { canCreateConsultations } = storeToRefs(useUserStore())

  if (!canCreateConsultations.value) {
    next('/')
  } else {
    next()
  }
}

export function hasReceiveConsultationsPermission(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const { canReceiveConsultations } = storeToRefs(useUserStore())

  if (!canReceiveConsultations.value) {
    next('/')
  } else {
    next()
  }
}

export async function isLegacyRoutingEnabledForHospital(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const hospitalStore = useHospitalsStore()
  const hospitalId = to.params.hospitalId as string | undefined

  if (!hospitalStore.currentHospital && hospitalId) {
    await hospitalStore.refreshHospitalById(hospitalId)
  }

  if (hospitalStore.currentHospital?.isRoutingV2Enabled === true) {
    return next('/')
  }

  next()
}

export function cleanKeycloakCallbackParamsGuard(to: any) {
  const hasKeycloakParams = KEYCLOAK_CALLBACK_PARAMS.some((p) => p in to.query)

  if (!hasKeycloakParams) return true

  const cleanQuery = Object.fromEntries(
    Object.entries(to.query).filter(([key]) => !KEYCLOAK_CALLBACK_PARAMS.includes(key as never))
  )

  return { ...to, query: cleanQuery, replace: true }
}

export function hasConnectPluginLicense(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const { isConnectEnabled } = storeToRefs(usePluginsStore())

  if (isConnectEnabled.value) {
    return next()
  }

  return next({
    name: to.path === '/' ? routeNames.VITAL_DATA : routeNames.PLUGIN_NOT_LICENSED
  })
}
